import { Box, Card, Divider, Flex, IconButton, Text, Tooltip } from 'components'
import { Cog6ToothIcon, GlobeAltIcon, PaintBrushIcon } from 'components/icons/hero-icons'
import { flatten, uniq } from 'lodash-es'
import { useState } from 'react'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { ITrait, TemplateRoute } from 'types'
import { forceLight } from 'utils'
import {
  ConnectionWithHighlight,
  getNodesWithConnections,
  getRoutings,
  getTemplateRouteNodes,
  getTemplateRoutes,
  matchStringArrays,
  TemplateRouteConnection,
  useFullTraitMap,
} from '../../../utils'
import { useTemplate } from '../TemplateProvider'

interface Props {
  hoveringRoutes: TemplateRoute[]
  onEditTrait: (traitKey: number) => void
  onHoverRoutes: (routes: TemplateRoute[]) => void
  onManageLayout: (layoutKey: number) => void
  onSelectRoute: (path: TemplateRoute, options?: { initialSelectedValueIds?: string[] }) => void
}

export default function TemplateTraitsViewer({
  hoveringRoutes,
  onEditTrait,
  onHoverRoutes,
  onManageLayout,
  onSelectRoute,
}: Props) {
  const { template } = useTemplate()
  const traitMap = useFullTraitMap()
  const [routeMenu, setRouteMenu] = useState<RouteMenu>({
    show: false,
    routes: [],
    position: { x: 0, y: 0 },
  })
  function handleConnectionClick(
    traitKey: number,
    connection: TemplateRouteConnection,
    event?: React.MouseEvent
  ) {
    const routes = getRoutesThroughConnection(traitKey, connection)

    if (routes.length === 1) {
      onSelectRoute(routes[0])
    } else if (routes.length > 1) {
      const position = { x: event?.clientX ?? 0, y: event?.clientY ?? 0 }
      setRouteMenu({ show: true, routes, position })
    }
  }

  function getRoutesThroughConnection(
    traitKey: number,
    connection: TemplateRouteConnection
  ): TemplateRoute[] {
    const { routing, layoutMap } = template || {}
    if (!routing || !layoutMap || !traitMap) return []

    const trait = traitMap[traitKey]
    const routes = getTemplateRoutes(routing)
    const matchingRoutes: TemplateRoute[] = []

    for (const route of routes) {
      const routings = getRoutings(routing, route)
      const matchingRoutingIndex = routings.findIndex(r => r.type === 'trait' && r.key === traitKey)
      const matchingRouting = routings[matchingRoutingIndex]
      if (!matchingRouting) continue

      const { default: defaultRoute, valueRoutes = [] } = matchingRouting
      if (connection === 'all') {
        if (defaultRoute && valueRoutes.length === 0) {
          matchingRoutes.push(route)
        }
      } else {
        const step = route[matchingRoutingIndex]
        if (step === 'default') {
          if (defaultRoute) {
            const usedValueIds = uniq(flatten(valueRoutes.map(vr => vr.fromIds)))
            const defaultIds = trait.values.filter(v => !usedValueIds.includes(v.id)).map(v => v.id)
            if (matchStringArrays(defaultIds, connection)) {
              matchingRoutes.push(route)
            }
          }
        } else if (typeof step === 'number') {
          const valueRoute = valueRoutes[step]
          if (valueRoute && matchStringArrays(valueRoute.fromIds, connection)) {
            matchingRoutes.push(route)
          }
        }
      }
    }

    return matchingRoutes
  }

  function handleLayoutClick(layoutKey: number, event?: React.MouseEvent) {
    const routes = getRoutesThroughLayout(layoutKey)

    if (routes.length === 1) {
      onSelectRoute(routes[0])
    } else if (routes.length > 1) {
      const position = { x: event?.clientX ?? 0, y: event?.clientY ?? 0 }
      setRouteMenu({ show: true, routes, position })
    }
  }

  function getRoutesThroughLayout(layoutKey: number) {
    const { routing, layoutMap } = template || {}
    if (!routing || !layoutMap) return []

    const layout = layoutMap[layoutKey]
    if (!layout) return []

    const routes = getTemplateRoutes(routing)
    return routes.filter(r => {
      const routings = getRoutings(routing, r)
      return routings.some(r => r.type === 'layout' && r.key === layoutKey)
    })
  }

  function handleRouteSelect(routePath: TemplateRoute) {
    onSelectRoute(routePath)
    setRouteMenu(prev => ({ ...prev, show: false }))
  }

  const { routing } = template || {}
  if (!routing || !traitMap) return null

  const nodesWithConnections = getNodesWithConnections(routing, traitMap, hoveringRoutes)
  const routeNodes = getTemplateRouteNodes(routing)
  const traitRouteNodes = routeNodes.filter(r => r.type === 'trait')
  const layoutRouteNodes = routeNodes.filter(r => r.type === 'layout')

  function getRouteToTrait(traitKey: number): TemplateRoute | undefined {
    const { routing } = template || {}
    if (!routing) return

    const routes = getTemplateRoutes(routing)
    for (const route of routes) {
      const routings = getRoutings(routing, route)
      const traitIndex = routings.findIndex(r => r.type === 'trait' && r.key === traitKey)
      if (traitIndex > -1) return route.slice(0, traitIndex)
    }
  }

  return (
    <Flex gap={3} flex={1} overflow="hidden">
      <ResponsiveMasonry
        columnsCountBreakPoints={Object.fromEntries(
          new Array(traitRouteNodes.length)
            .fill(0)
            .map((_, index) => [800 + index * 250, index + 1])
        )}
        style={{ flex: 1, overflow: 'auto' }}
      >
        <Masonry>
          {traitRouteNodes.map(r => {
            const trait = traitMap[r.key]
            if (!trait) return null

            const connections =
              nodesWithConnections.find(c => c.type === 'trait' && c.key === r.key)?.connections ??
              []
            return (
              <Trait
                key={r.key}
                connections={connections}
                onEditTrait={() => onEditTrait(r.key)}
                onNonRoutedValueClick={valueId => {
                  const routeToTrait = getRouteToTrait(r.key)
                  if (routeToTrait) {
                    onSelectRoute(routeToTrait, { initialSelectedValueIds: [valueId] })
                  }
                }}
                onConnectionHover={connection =>
                  onHoverRoutes(getRoutesThroughConnection(r.key, connection))
                }
                onConnectionClick={(connection, event) =>
                  handleConnectionClick(r.key, connection, event)
                }
                trait={trait}
              />
            )
          })}
        </Masonry>
      </ResponsiveMasonry>
      <Divider orientation="vertical" />
      <Card
        {...forceLight}
        direction="column"
        alignSelf="flex-start"
        minWidth="15%"
        header={
          <Text px={4} py={3} textStyle="h6">
            Layouts
          </Text>
        }
        body={
          <Flex direction="column">
            {layoutRouteNodes.map((r, index) => {
              const layout = template?.layoutMap?.[r.key]
              if (!layout) return null

              const connections =
                nodesWithConnections.find(c => c.type === 'layout' && c.key === r.key)
                  ?.connections ?? []
              const highlight = connections.some(c => c.highlight)

              return (
                <Flex
                  key={r.key}
                  alignItems="center"
                  gap={2}
                  px={4}
                  py={3}
                  bg={highlight ? 'primary.100' : 'transparent'}
                  _hover={{ bg: 'primary.100' }}
                  borderBottomWidth={index < layoutRouteNodes.length - 1 ? 1 : 0}
                  borderBottomColor="gray.100"
                  onMouseEnter={() => onHoverRoutes(getRoutesThroughLayout(r.key))}
                  onMouseLeave={() => onHoverRoutes([])}
                >
                  <Box
                    cursor="pointer"
                    flex={1}
                    minWidth={0}
                    onClick={e => handleLayoutClick(r.key, e)}
                  >
                    <Text>{layout.label || `Layout ${index + 1}`}</Text>
                  </Box>
                  <Tooltip content="Manage template layout">
                    <IconButton
                      aria-label="Manage template layout"
                      icon={<PaintBrushIcon boxSize={4} type="outline" />}
                      onClick={e => {
                        e.stopPropagation()
                        onManageLayout(r.key)
                      }}
                      size="sm"
                      variant="ghost"
                    />
                  </Tooltip>
                </Flex>
              )
            })}
          </Flex>
        }
      />

      {routeMenu.show && (
        <>
          <Box
            position="fixed"
            inset={0}
            zIndex={999}
            onClick={() => setRouteMenu(prev => ({ ...prev, show: false }))}
          />
          <Card
            position="fixed"
            left={routeMenu.position.x}
            top={routeMenu.position.y}
            zIndex={1000}
            minWidth="120px"
            p={0}
            shadow="lg"
            overflow="hidden"
            bodyProps={{ p: 0 }}
            body={
              <Flex direction="column">
                {routeMenu.routes.map((path, index) => (
                  <Box
                    key={`route-${index}`}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    borderBottomWidth={index < routeMenu.routes.length - 1 ? 1 : 0}
                    borderBottomColor="gray.100"
                    onClick={() => handleRouteSelect(path)}
                    onMouseEnter={() => onHoverRoutes([path])}
                    onMouseLeave={() => onHoverRoutes([])}
                  >
                    <Text fontSize="sm">Route {index + 1}</Text>
                  </Box>
                ))}
              </Flex>
            }
          />
        </>
      )}
    </Flex>
  )
}

interface RouteMenu {
  show: boolean
  routes: TemplateRoute[]
  position: { x: number; y: number }
}

interface TraitProps {
  connections: ConnectionWithHighlight[]
  onEditTrait: () => void
  onConnectionClick: (connection: TemplateRouteConnection, event: React.MouseEvent) => void
  onConnectionHover: (connections: TemplateRouteConnection) => void
  onNonRoutedValueClick: (valueId: string) => void
  trait: ITrait
}

function Trait({
  connections,
  onEditTrait,
  onConnectionClick,
  onConnectionHover,
  onNonRoutedValueClick,
  trait,
}: TraitProps) {
  function renderContent(connection: TemplateRouteConnection) {
    if (connection === 'all') return 'All Traits'

    if (!Array.isArray(connection)) return ''

    const valueIds = connection.slice(0, 5)
    const remainingCount = connection.length - 5

    const valueNames = valueIds
      .map(id => trait.values.find(v => v.id === id)?.title.en)
      .filter(Boolean)

    let content = valueNames.join(', ')

    if (remainingCount > 0) content += ` (+${remainingCount} other items)`

    return content
  }

  const hasAllConnection = connections.some(({ value }) => value === 'all')
  const routedValueIds = hasAllConnection
    ? trait.values.map(value => value.id)
    : uniq(connections.flatMap(({ value }) => (Array.isArray(value) ? value : [])).filter(Boolean))
  const nonRoutedValues = trait.values.filter(value => !routedValueIds.includes(value.id))

  return (
    <Card
      {...forceLight}
      flex={1}
      header={
        <Flex justifyContent="space-between" alignItems="center" px={4} py={3}>
          <Flex alignItems="center" gap={1}>
            {trait.id && (
              <Tooltip content="Trait shared within workspace">
                <Box as="span" display="inline-flex">
                  <GlobeAltIcon type="outline" boxSize={4} color="primary.500" />
                </Box>
              </Tooltip>
            )}
            <Text textStyle="h6">{trait.title.en}</Text>
          </Flex>
          <IconButton
            icon={<Cog6ToothIcon boxSize={4} />}
            variant="ghost"
            size="sm"
            onClick={onEditTrait}
            aria-label="Edit trait"
          />
        </Flex>
      }
      body={
        <Box position="relative">
          <Flex flexDirection="column" p={0}>
            {connections.map(({ value, highlight }, index) => (
              <Box
                key={`node-${index}`}
                px={4}
                py={3}
                cursor="pointer"
                bg={highlight ? 'primary.100' : 'transparent'}
                _hover={{ bg: 'primary.100' }}
                borderBottomWidth={index < connections.length - 1 ? 1 : 0}
                borderBottomColor="gray.100"
                onMouseEnter={() => onConnectionHover(value)}
                onMouseLeave={() => onConnectionHover([])}
                onClick={event => onConnectionClick(value, event)}
              >
                <Box fontSize="sm" color="gray.700">
                  {renderContent(value)}
                </Box>
              </Box>
            ))}

            {nonRoutedValues.length > 0 && (
              <>
                {connections.length > 0 && <Divider />}
                <Box px={4} py={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="primary.800">
                    Non routed
                  </Text>
                </Box>
                {nonRoutedValues.map((value, index) => (
                  <Box
                    key={`non-routed-${value.id}`}
                    px={4}
                    py={3}
                    cursor="pointer"
                    _hover={{ bg: 'primary.100' }}
                    borderBottomWidth={index < nonRoutedValues.length - 1 ? 1 : 0}
                    borderBottomColor="gray.100"
                    onClick={() => onNonRoutedValueClick(value.id)}
                  >
                    <Text fontSize="sm" color="primary.800">
                      {value.title.en}
                    </Text>
                  </Box>
                ))}
              </>
            )}
          </Flex>
        </Box>
      }
    />
  )
}
